import React, { PropTypes } from 'react';
import { Header, Form, Radio, Segment, Input, Button } from 'semantic-ui-react';
import Datepicker from 'common/components/Datepicker';
import Dropdown from 'common/components/Dropdown';
import cx from 'classnames';
import moment from 'moment';
import { DATE_FORMAT } from 'common/components/PreplanificationSelection';
import { paymentConditionOptions } from '../../index';

const DepositInvoice = ({
  onValueChange,
  depositInvoice,
  depositPercentage,
  depositInvoiceCreateAction,
  depositInvoiceDate,
  depositInvoicePaymentDate,
  depositInvoicePaymentCondition,
  errors,
}) => {
  const isDepositPaymentDateDisable = () => {
    const selectedValue = paymentConditionOptions.find(
      r => r.value === depositInvoicePaymentCondition
    );

    if (selectedValue) {
      return selectedValue.inputDateDisabled;
    }
    return false;
  };

  const itemDepositClassName = value => {
    const active = depositInvoicePaymentCondition === value;
    return cx('item', {
      active: active,
      selected: active,
    });
  };

  const renderDepositDefault = () => {
    const selectedValue = paymentConditionOptions.find(
      r => r.value === depositInvoicePaymentCondition
    );
    if (selectedValue) {
      return <div className="text">{selectedValue.text}</div>;
    }
    return <div className="default text"></div>;
  };

  const renderDepositItem = item => (
    <div
      key={item.value}
      data-value={item.value}
      className={itemDepositClassName(item.value)}
    >
      {item.text}
    </div>
  );

  const updateDepositPaymentDate = (date, condition) => {
    let updatedDate = moment(date, DATE_FORMAT).add(
      'days',
      condition.numberOfDays
    );

    if (condition.endOfMonth) {
      updatedDate.endOf('month');
    }

    onValueChange(
      'invoicingInformations.depositInvoicePaymentDate',
      updatedDate.format(DATE_FORMAT)
    );
  };

  const onDepositDropdownChange = (value, text, choice) => {
    onValueChange(
      'invoicingInformations.depositInvoicePaymentCondition',
      value
    );
    const selectedValue = paymentConditionOptions.find(r => r.value === value);
    if (
      selectedValue &&
      selectedValue.value !== 'CUSTOM' &&
      depositInvoiceDate
    ) {
      updateDepositPaymentDate(depositInvoiceDate, selectedValue);
    }
  };

  const fieldClass = fieldName =>
    cx('field required', {
      error: errors.includes(fieldName),
    });

  const invoiceDateLabel = isCreateAction => {
    return isCreateAction ? "Date d'émission estimée" : "Date d'émission";
  };

  const invoicePaymentDateLabel = isCreateAction => {
    return isCreateAction ? 'Date de réglement estimée' : 'Date de réglement';
  };

  const onDepositInvoiceChange = (_, value) => {
    onValueChange('invoicingInformations.depositPercentage', 0);
    onValueChange('invoicingInformations.depositInvoice', value.checked);
  };

  const onDepositPercentageChange = (_, input) => {
    let value = input.value;
    if (value && value !== '') {
      value = parseInt(input.value, 0);
    }
    onValueChange('invoicingInformations.depositPercentage', value);
  };

  const onDepositInvoiceDateChange = date => {
    onValueChange(
      'invoicingInformations.depositInvoiceDate',
      moment(date.date).format(DATE_FORMAT)
    );
    const selectedValue = paymentConditionOptions.find(
      r => r.value === depositInvoicePaymentCondition
    );
    if (selectedValue && selectedValue.value !== 'CUSTOM') {
      updateDepositPaymentDate(
        moment(date.date).format(DATE_FORMAT),
        selectedValue
      );
    }
  };

  const onDepositInvoicePaymentDateChange = date => {
    onValueChange(
      'invoicingInformations.depositInvoicePaymentDate',
      moment(date.date).format(DATE_FORMAT)
    );
  };

  const onDepositCreateInvoiceActionChange = event => {
    onValueChange(
      'invoicingInformations.depositInvoiceCreateAction',
      event.target.value === 'true'
    );
    if (event.target.value !== 'true') {
      onValueChange(
        'invoicingInformations.depositInvoiceDate',
        moment().format(DATE_FORMAT)
      );
      const selectedValue = paymentConditionOptions.find(
        r => r.value === depositInvoicePaymentCondition
      );
      if (selectedValue && selectedValue.value !== 'CUSTOM') {
        updateDepositPaymentDate(moment().format(DATE_FORMAT), selectedValue);
      }
    }
  };

  return (
    <Segment>
      <Header size="tiny">4- Préparer la facture d'acompte</Header>
      <Form.Group>
        <Form.Field
          required
          width={5}
          className={fieldClass('depositPercentage')}
          disabled={!depositInvoice}
        >
          <label>Acompte en %</label>
          <Input
            name="depositPercentage"
            value={depositPercentage | 0}
            onChange={onDepositPercentageChange}
          />
        </Form.Field>
        <Form.Field width={13} disabled={!depositInvoice}>
          <label>Payable en</label>
          En une fois
        </Form.Field>
        <Form.Field width={2}>
          <Radio
            toggle
            name="depositInvoice"
            checked={depositInvoice}
            onChange={onDepositInvoiceChange}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field required disabled={!depositInvoice}>
          <label>Action à appliquer sur la facture</label>
          <Button.Group>
            <Button
              type="button"
              positive={depositInvoiceCreateAction}
              value={true}
              onClick={onDepositCreateInvoiceActionChange}
            >
              Créer
            </Button>
            <Button
              type="button"
              positive={!depositInvoiceCreateAction}
              value={false}
              onClick={onDepositCreateInvoiceActionChange}
            >
              Emettre
            </Button>
          </Button.Group>
        </Form.Field>
        <Form.Field
          required
          disabled={!depositInvoice}
          className={fieldClass('invoicingInformations.depositInvoiceDate')}
        >
          <label>{invoiceDateLabel(depositInvoiceCreateAction)}</label>
          <Datepicker
            identifier="depositInvoiceDate"
            name="depositInvoiceDate"
            value={depositInvoiceDate}
            onChange={onDepositInvoiceDateChange}
            placeholder="JJ/MM/AAAA"
            disabled={!depositInvoiceCreateAction}
          />
        </Form.Field>
        <Form.Field
          required
          disabled={!depositInvoice}
          className={fieldClass(
            'invoicingInformations.depositInvoicePaymentCondition'
          )}
        >
          <label>Conditions de règlement</label>
          <Dropdown
            name="depositInvoicePaymentCondition"
            value={depositInvoicePaymentCondition}
            items={paymentConditionOptions}
            renderDefault={renderDepositDefault}
            renderItem={renderDepositItem}
            onChange={onDepositDropdownChange}
          />
        </Form.Field>
        <Form.Field
          required
          disabled={!depositInvoice}
          className={fieldClass(
            'invoicingInformations.depositInvoicePaymentDate'
          )}
        >
          <label>{invoicePaymentDateLabel(depositInvoiceCreateAction)}</label>
          <Datepicker
            identifier="depositInvoicePaymentDate"
            name="depositInvoicePaymentDate"
            value={depositInvoicePaymentDate}
            onChange={onDepositInvoicePaymentDateChange}
            placeholder="JJ/MM/AAAA"
            disabled={isDepositPaymentDateDisable()}
          />
        </Form.Field>
      </Form.Group>
    </Segment>
  );
};

DepositInvoice.propTypes = {
  onValueChange: PropTypes.func.isRequired,
  depositInvoiceCreateAction: PropTypes.bool.isRequired,
  depositInvoiceDate: PropTypes.string,
  depositInvoicePaymentDate: PropTypes.string,
  depositInvoicePaymentCondition: PropTypes.string.isRequired,
  errors: PropTypes.array,
};

export default DepositInvoice;



// WEBPACK FOOTER //
// ./src/modules/saas/aast/case-contract/organize/components/DepositInvoice.js